import type { Banner } from './useBanner';

const COMMON_BANNER_PROPS: Partial<Banner> = {
  global: true,
  persistent: true,
};

/**
 * Manages the global status banner based on subscription and cancellation state.
 * Subscription-level statuses (ENDED, OVERDUE, SUSPENDED) take priority over
 * cancellation statuses to reflect the most actionable state to the user.
 */
export function useSubscriptionBanner(): void {
  const { t, locale } = useI18n();
  const { setBanner, clearBanner } = useBanner();
  const localePath = useLocalePath();

  const subscriptionStore = useSubscriptionStore();
  const { subscriptionStatus, cancelStatus, cancellationDate, isKeepPlanOpen, selectedSubscription: subscription } = storeToRefs(subscriptionStore);
  const { handleRetryPayment } = useOutstandingBalance();

  const cancellationDateFormatted = useDateFormat(cancellationDate, { locale });
  const subscriptionStartDate = computed(() => (subscription.value?.quota?.periodStart ? new Date(subscription.value.quota.periodStart) : undefined));
  const subscriptionStartDateFormatted = useDateFormat(subscriptionStartDate, { dateFormat: 'full', locale });

  watch(
    [subscriptionStatus, cancelStatus, cancellationDateFormatted, subscriptionStartDateFormatted],
    ([subStatus, cancellationStatus, cancelDate, subStartDate]) => {
      if (!subStatus) {
        return clearBanner();
      }

      switch (subStatus) {
        case 'OVERDUE':
        case 'SUSPENDED':
          setBanner({
            ...COMMON_BANNER_PROPS,
            type: 'danger',
            message: t(`ui.global.status.${subStatus.toLowerCase()}`),
            primaryAction: {
              label: t('ui.booking.search.retryButton.text'),
              handler: () => handleRetryPayment(),
            },
            secondaryAction: {
              label: t('ui.billingPayment.changeCard.title'),
              handler: () => navigateTo(localePath('/billing/change-card')),
            },
          });
          return;
        case 'CANCELLED':
          setBanner({
            ...COMMON_BANNER_PROPS,
            type: 'warning',
            message: t('ui.global.status.cancelled'),
          });
          return;
        case 'PENDING_ACTIVATION':
          setBanner({
            ...COMMON_BANNER_PROPS,
            type: 'info',
            message: t('ui.global.status.pendingActivation', { date: subStartDate }),
          });
          return;
        default:
          break;
      }

      switch (cancellationStatus) {
        case 'PENDING':
          setBanner({
            ...COMMON_BANNER_PROPS,
            type: 'warning',
            message: t('ui.global.status.pendingCancelation', { date: cancelDate }),
            primaryAction: {
              label: t('ui.subscription.cancelPlan.keepPlan.button'),
              handler: () => {
                isKeepPlanOpen.value = true;
              },
            },
          });
          return;
        case 'CANCELLED':
          setBanner({
            ...COMMON_BANNER_PROPS,
            type: 'warning',
            message: t('ui.subscription.cancelPlan.cancelledInformation'),
          });
          return;
        default:
          break;
      }

      clearBanner();
    },
    { immediate: true },
  );
}
